import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from "react"
import { API } from "../config/api"
import { useDealerGroup } from "./DealerGroupContext"

const TOKEN_KEY = "invi_token"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface InventoryRun {
  run_id: number
  completed_at: string
  discrepancies_count: number
  pending_count: number
  completed_count: number
  unclassified_count: number
  dealer_duplicates_count: number
  is_fresh: boolean
}

export interface WebsiteRun {
  run_id: number
  completed_at: string
  matched_count: number
  missing_count: number
  should_remove_count: number
}

export interface RunSummary {
  inventory: InventoryRun | null
  website: WebsiteRun | null
}

interface RunsContextType {
  data: RunSummary | null
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
  secondsUntilRefresh: number
}

const REFRESH_INTERVAL_MS = 60_000
const REFRESH_INTERVAL_SECONDS = Math.floor(REFRESH_INTERVAL_MS / 1000)

const RunsContext = createContext<RunsContextType | undefined>(undefined)

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const RunsProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [data, setData] = useState<RunSummary | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [secondsUntilRefresh, setSecondsUntilRefresh] = useState<number>(
    REFRESH_INTERVAL_SECONDS
  )

  // The header timestamps reflect the active dealer group (null = All Groups →
  // the global latest for an operator). /runs/latest is login-required.
  const { dealerGroupId } = useDealerGroup()

  const inFlightRef = useRef<boolean>(false)

  const refresh = useCallback(async () => {
    if (inFlightRef.current) return
    const token = localStorage.getItem(TOKEN_KEY)
    // Unauthenticated (e.g. login page): nothing to show, skip the fetch.
    if (!token) {
      setLoading(false)
      return
    }
    inFlightRef.current = true
    setLoading(true)
    const startTime = Date.now()
    try {
      const groupParam = dealerGroupId != null ? `?dealer_group_id=${dealerGroupId}` : ""
      const resp = await fetch(`${API.baseUrl}/runs/latest${groupParam}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
      const json = (await resp.json()) as RunSummary
      setData(json)
      setError(null)
    } catch (e: any) {
      setError(e?.message || "Failed to fetch runs")
    } finally {
      // Ensure loading state is visible for at least 800ms
      const elapsed = Date.now() - startTime
      const remaining = Math.max(0, 800 - elapsed)
      await new Promise((resolve) => setTimeout(resolve, remaining))
      setLoading(false)
      setSecondsUntilRefresh(REFRESH_INTERVAL_SECONDS)
      inFlightRef.current = false
    }
  }, [dealerGroupId])

  // Initial fetch
  useEffect(() => {
    refresh()
  }, [refresh])

  // 1-second countdown; triggers refresh when it hits zero
  useEffect(() => {
    const tick = setInterval(() => {
      setSecondsUntilRefresh((s) => {
        if (s <= 1) {
          refresh()
          return REFRESH_INTERVAL_SECONDS
        }
        return s - 1
      })
    }, 1000)
    return () => clearInterval(tick)
  }, [refresh])

  const value: RunsContextType = {
    data,
    loading,
    error,
    refresh,
    secondsUntilRefresh,
  }

  return <RunsContext.Provider value={value}>{children}</RunsContext.Provider>
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useRuns(): RunsContextType {
  const ctx = useContext(RunsContext)
  if (!ctx) {
    throw new Error("useRuns must be used within a RunsProvider")
  }
  return ctx
}
